import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';

export default function PdfExportModal({ open, onClose, onExport, whitelabelSettings, title = 'Export PDF' }) {
  const navigate = useNavigate();
  const [extraText, setExtraText] = useState('');
  const [exporting, setExporting] = useState(null);

  if (!open) return null;

  const hasWhitelabel = Boolean(whitelabelSettings?.firm_name || whitelabelSettings?.logo_url);

  const handleExport = async (type) => {
    setExporting(type);
    try {
      await onExport({
        whitelabel: type === 'whitelabel',
        extraText,
      });
      onClose();
      setExtraText('');
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
      <div className="w-full max-w-lg rounded-2xl border border-bg-300 bg-bg-200 shadow-xl">
        <div className="flex items-center justify-between border-b border-bg-300 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-text-100">{title}</h2>
            <p className="mt-1 text-xs text-text-300">Choose the standard SharpeMetrix PDF or a whitelabel version.</p>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-text-300 hover:bg-bg-300 hover:text-text-100">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          <div className="rounded-xl bg-bg-100 p-4">
            <p className="text-sm font-medium text-text-100">Whitelabel settings</p>
            <p className="mt-1 text-xs text-text-300">
              {hasWhitelabel
                ? `${whitelabelSettings?.firm_name || 'Your firm'} branding will be used.`
                : 'No settings found yet. Add a firm name, logo, and colors in Profile before exporting a whitelabel factsheet.'}
            </p>
            {!hasWhitelabel && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigate('/profile');
                }}
                className="mt-3 rounded-lg bg-primary-100 px-3 py-2 text-xs font-semibold text-white hover:bg-primary-200"
              >
                Go to Profile Settings
              </button>
            )}
          </div>

          <div className="rounded-xl border border-bg-300 bg-bg-100 p-4">
            <p className="text-sm font-medium text-text-100">Logo recommendations</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-text-300">
              <li>Use a transparent PNG or SVG where possible.</li>
              <li>Recommended aspect ratio is wide, around 4:1 to 6:1.</li>
              <li>Suggested size: at least 600px wide, under 1MB if possible.</li>
              <li>Avoid white-only logos unless your primary color is dark enough for contrast.</li>
            </ul>
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-widest text-text-300">
              Optional whitelabel final-page text
            </label>
            <textarea
              value={extraText}
              onChange={(event) => setExtraText(event.target.value)}
              rows={5}
              placeholder="Optional notes or compliance language. This appears on the final page after the model tables and methodology."
              className="mt-2 block w-full rounded-lg bg-bg-100 px-4 py-2.5 text-sm text-text-100 ring-1 ring-bg-300 outline-none transition-all focus:ring-2 focus:ring-primary-100"
            />
            <p className="mt-2 text-xs text-text-300">
              If left blank, your default profile final-page text will be used for whitelabel exports.
            </p>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-bg-300 px-6 py-4 sm:flex-row sm:justify-end">
          <button
            onClick={() => handleExport('regular')}
            disabled={!!exporting}
            className="rounded-lg bg-bg-100 px-4 py-2 text-sm font-medium text-text-100 hover:bg-bg-300 disabled:opacity-60"
          >
            {exporting === 'regular' ? 'Exporting...' : 'Regular PDF'}
          </button>
          <button
            onClick={() => handleExport('whitelabel')}
            disabled={!!exporting}
            className="rounded-lg bg-primary-100 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-200 disabled:opacity-60"
          >
            {exporting === 'whitelabel' ? 'Exporting...' : 'Whitelabel PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}
